import * as React from 'react';
import * as styled from './styled';
import {getFolders as getCourseFolders} from 'api/course';
import {getFolders as getLessonFolders} from 'api/lesson/requests';
import {Folder} from './components/Folder';
import {CreateFolder} from './components/CreateFolder';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import useModal from 'hooks/use-modal';
import {IconPlus} from 'icons';
import {FolderSelectorProps} from './types';
import {ErrorObject, Folder as FolderType} from 'types';

/**
 * The component renderer folder selector.
 * @param {FolderSelectorProps} props
 * @return {React.ReactElement<FolderSelectorProps>}
 * @constructor
 */
export default function FolderSelector(
    props: FolderSelectorProps,
): React.ReactElement<FolderSelectorProps> {
  const [
    folders, setFolders,
  ] = React.useState<FolderType[]>(
    []
  );

  const [
    activeFolders, setActiveFolders,
  ] = React.useState<FolderType[]>(
    props.folders || []
  );

  const CreateFolderModal = useModal();

  /**
   * The hook of set active folders.
   * @param {FolderType[]} folders
   */
  function _setActiveFolders(folders: FolderType[]): void {
    setActiveFolders(folders);
    props.onChange && props.onChange(folders);
  }

  /**
   * Fetch the folder's.
   */
  function fetch(): void {
    /**
     * Update the folders store.
     * @param {FolderType[] | ErrorObject | undefined} response
     */
    function updateFolders(
      response:
        | FolderType[]
        | undefined
        | ErrorObject
    ): void {
      validateResponse(response);

      if (response && !isError(response)) {
        setFolders(response);
      }
    }

    switch (props.type) {
      case 'courses': {
        getCourseFolders().then(updateFolders)
        break;
      }
      case 'lessons': {
        getLessonFolders().then(updateFolders)
        break;
      }
    }
  }

  React.useEffect(() => {
    fetch();
  }, []);

  /**
   * Render total.
   * @return {JSX.Element}
   */
  function renderTotal(): JSX.Element {
    /**
     * Is the folder active.
     * @return {boolean}
     */
    function isActive(): boolean {
      return activeFolders.length === 0;
    }

    /**
     * The click event handler.
     */
    function handleClick(): void {
      _setActiveFolders([]);
    }

    return (
      <Folder
        name="Все"
        isActive={isActive()}
        onClick={handleClick}
      />
    );
  }

  /**
   * Render folders.
   * @return {JSX.Element}
   */
  function renderFolders(): JSX.Element[] {
    /**
     * Render folder.
     * @param {FolderType} folder
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderFolder(folder: FolderType, index: number): JSX.Element {
      /**
       * Is the folder active.
       * @return {boolean}
       */
      function isActive(): boolean {
        return Boolean(activeFolders.find((activeFolder) => (
          folder === activeFolder
        )));
      }

      /**
       * The click event handler.
       */
      function handleClick(): void {
        if (isActive()) {
          _setActiveFolders(activeFolders.filter((activeFolder) => (
            activeFolder !== folder
          )))
        } else {
          _setActiveFolders(props.creatable ? [
            ...activeFolders,
            folder,
          ] : [folder]);
        }
      }

      return (
        <Folder
          key={index}
          name={folder}
          isActive={isActive()}
          onClick={handleClick}
        />
      );
    }

    return folders.map((folder, index) => (
      renderFolder(folder, index)
    ));
  }

  /**
   * Render button.
   * @return {JSX.Element | undefined}
   */
  function renderButton(): JSX.Element | undefined {
    /**
     * The click event handler.
     */
    function handleClick(): void {
      CreateFolderModal.setModalVisible(true);
    }

    /**
     * The blur event handler.
     */
    function handleBlur(): void {
      CreateFolderModal.setModalVisible(false);
    }

    /**
     * The submit create folder event handler.
     */
    function handleSubmit(name: string): void {
      CreateFolderModal.setModalVisible(false);

      _setActiveFolders([
        ...activeFolders,
        name,
      ]);

      setFolders([
        ...folders,
        name,
      ]);
    }

    return props.creatable ? (
      <React.Fragment>
        <styled.Button onClick={handleClick}>
          <IconPlus />
        </styled.Button>
        {CreateFolderModal.isModalVisible && (
          <CreateFolder
            onSubmit={handleSubmit}
            onBlur={handleBlur}
          />
        )}
      </React.Fragment>
    ) : undefined;
  }

  return (
    <styled.Wrapper>
      {renderTotal()}
      {renderFolders()}
      {renderButton()}
    </styled.Wrapper>
  );
}
